import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Search,
  Users,
  ListChecks,
  CreditCard,
  UserCog,
  RefreshCw,
  ArrowUpRight,
  Target,
  FolderKanban,
  Megaphone,
  FileText,
  FileSignature,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { NAV_ITEMS } from "@/lib/constants";
import { can, isAgencyAdmin } from "@/lib/permissions";
import { useCurrentUser } from "@/features/auth/auth.hooks";
import { useClients } from "@/features/clients/clients.hooks";
import { useTasks } from "@/features/tasks/tasks.hooks";
import { usePayments } from "@/features/payments/payments.hooks";
import { useTeamMembers } from "@/features/team/team.hooks";
import { useSubscriptions } from "@/features/subscriptions/subscriptions.hooks";
import { useLeads } from "@/features/leads/leads.hooks";
import { useProjects } from "@/features/projects/projects.hooks";
import { useCampaigns } from "@/features/campaigns/campaigns.hooks";
import { useInvoices, useQuotes } from "@/features/invoices/invoices.hooks";

type ResultGroup =
  | "pages"
  | "clients"
  | "leads"
  | "projects"
  | "tasks"
  | "campaigns"
  | "invoices"
  | "quotes"
  | "payments"
  | "team"
  | "subscriptions";

interface SearchResult {
  group: ResultGroup;
  id: string;
  label: string;
  sublabel?: string;
  to: string;
}

const GROUP_ICON: Record<ResultGroup, typeof Search> = {
  pages: ArrowUpRight,
  clients: Users,
  leads: Target,
  projects: FolderKanban,
  tasks: ListChecks,
  campaigns: Megaphone,
  invoices: FileText,
  quotes: FileSignature,
  payments: CreditCard,
  team: UserCog,
  subscriptions: RefreshCw,
};

const GROUP_ORDER: ResultGroup[] = [
  "pages",
  "clients",
  "leads",
  "projects",
  "tasks",
  "campaigns",
  "invoices",
  "quotes",
  "payments",
  "team",
  "subscriptions",
];
const MAX_PER_GROUP = 5;

/**
 * Chaque groupe d'entités n'est cherché que si l'utilisateur peut OUVRIR la
 * page correspondante — même règle RBAC que le menu, pour que la recherche ne
 * propose jamais une destination qui répondrait « accès refusé ».
 */
const GROUP_ROUTE: Record<Exclude<ResultGroup, "pages">, string> = {
  clients: "/clients",
  leads: "/leads",
  projects: "/projects",
  tasks: "/tasks",
  campaigns: "/marketing/campagnes",
  invoices: "/invoices",
  quotes: "/invoices",
  payments: "/invoices",
  team: "/team",
  subscriptions: "/subscriptions",
};

function matches(query: string, ...fields: (string | undefined | null)[]): boolean {
  return fields.some((f) => f && f.toLowerCase().includes(query));
}

/**
 * Command palette (refonte spec §5): Ctrl/⌘+K focuses it from anywhere; it
 * opens any page (RBAC-filtered NAV_ITEMS) and searches the loaded entities.
 * Frontend-only.
 *
 * Un résultat mène à l'ÉLÉMENT quand une route de détail existe (tâche,
 * membre, client), sinon à sa page — et jamais à une route morte : l'ancien
 * groupe « paiements » envoyait vers `/payments`, retirée du routeur depuis la
 * spec Phase 1 ; il pointe désormais sur l'onglet Paiements de la Facturation.
 */
export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data: currentUser } = useCurrentUser();
  const user = currentUser ?? null;
  const { clients } = useClients();
  const { tasks } = useTasks();
  const { payments } = usePayments();
  const { members } = useTeamMembers();
  const { subscriptions } = useSubscriptions();
  const { leads } = useLeads();
  const { projects } = useProjects();
  const { campaigns } = useCampaigns();
  const { invoices } = useInvoices();
  const { quotes } = useQuotes();

  // Global Ctrl/⌘+K → focus the palette (spec: open any page without the sidebar).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const pages = useMemo(
    () =>
      user
        ? NAV_ITEMS.filter(
            (i) =>
              (!i.permission || can(user, i.permission)) &&
              (!i.adminOnly || isAgencyAdmin(user)) &&
              (!i.restrictRoles || i.restrictRoles.some((r) => user.roles.includes(r as never))),
          )
        : [],
    [user],
  );

  // Routes réellement ouvertes à cet utilisateur (dérivées du menu RBAC).
  const allowedRoutes = useMemo(() => new Set(pages.map((p) => p.to)), [pages]);
  const allowed = (group: Exclude<ResultGroup, "pages">) =>
    allowedRoutes.has(GROUP_ROUTE[group]);

  const results = useMemo<SearchResult[]>(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 1) return [];
    const out: SearchResult[] = [];
    const push = (r: SearchResult) => out.push(r);

    for (const p of pages
      .filter((p) => matches(q, t(p.labelKey), p.label))
      .slice(0, MAX_PER_GROUP)) {
      push({ group: "pages", id: p.to, label: t(p.labelKey), sublabel: p.to, to: p.to });
    }
    if (allowed("clients"))
      for (const c of clients.filter((c) => matches(q, c.name, c.email)).slice(0, MAX_PER_GROUP)) {
        push({ group: "clients", id: c.id, label: c.name, sublabel: c.email, to: `/clients/${c.id}` });
      }
    if (allowed("leads"))
      for (const l of leads.filter((l) => matches(q, l.name, l.email, l.phone)).slice(0, MAX_PER_GROUP)) {
        push({ group: "leads", id: l.id, label: l.name, sublabel: l.email ?? l.phone, to: "/leads" });
      }
    if (allowed("projects"))
      for (const p of projects.filter((p) => matches(q, p.name, p.clientName)).slice(0, MAX_PER_GROUP)) {
        push({ group: "projects", id: p.id, label: p.name, sublabel: p.clientName, to: "/projects" });
      }
    if (allowed("tasks"))
      for (const tk of tasks.filter((tk) => matches(q, tk.title, tk.clientName, tk.assignee)).slice(0, MAX_PER_GROUP)) {
        // Route de détail réelle — la liste forçait l'utilisateur à rechercher
        // une seconde fois la même tâche une fois sur la page.
        push({ group: "tasks", id: tk.id, label: tk.title, sublabel: tk.clientName, to: `/tasks/${tk.id}` });
      }
    if (allowed("campaigns"))
      for (const c of campaigns.filter((c) => matches(q, c.name, c.clientName, c.platform)).slice(0, MAX_PER_GROUP)) {
        push({ group: "campaigns", id: c.id, label: c.name, sublabel: c.clientName, to: "/marketing/campagnes" });
      }
    if (allowed("invoices"))
      for (const i of invoices.filter((i) => matches(q, i.number, i.clientName)).slice(0, MAX_PER_GROUP)) {
        push({ group: "invoices", id: i.id, label: i.number, sublabel: i.clientName, to: "/invoices" });
      }
    if (allowed("quotes"))
      for (const qu of quotes.filter((qu) => matches(q, qu.number, qu.clientName)).slice(0, MAX_PER_GROUP)) {
        push({ group: "quotes", id: qu.id, label: qu.number, sublabel: qu.clientName, to: "/invoices?tab=quotes" });
      }
    if (allowed("payments"))
      for (const p of payments.filter((p) => matches(q, p.clientName, p.note, p.period)).slice(0, MAX_PER_GROUP)) {
        push({ group: "payments", id: p.id, label: p.clientName, sublabel: p.note ?? p.period, to: "/invoices?tab=payments" });
      }
    if (allowed("team"))
      for (const m of members.filter((m) => matches(q, m.name, m.email, m.role)).slice(0, MAX_PER_GROUP)) {
        push({ group: "team", id: m.id, label: m.name, sublabel: m.email, to: `/team/${m.id}` });
      }
    if (allowed("subscriptions"))
      for (const s of subscriptions.filter((s) => matches(q, s.name, s.provider)).slice(0, MAX_PER_GROUP)) {
        push({ group: "subscriptions", id: s.id, label: s.name, sublabel: s.provider, to: "/subscriptions" });
      }
    return out;
  }, [query, pages, allowedRoutes, clients, leads, projects, tasks, campaigns, invoices, quotes, payments, members, subscriptions, t]);

  const grouped = useMemo(() => {
    const map = new Map<ResultGroup, SearchResult[]>();
    for (const r of results) {
      const arr = map.get(r.group) ?? [];
      arr.push(r);
      map.set(r.group, arr);
    }
    return map;
  }, [results]);

  const go = (r: SearchResult) => {
    navigate(r.to);
    setQuery("");
    setOpen(false);
  };

  const showDropdown = open && query.trim().length >= 1;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      (e.target as HTMLInputElement).blur();
      return;
    }
    if (!showDropdown || results.length === 0) {
      if (e.key === "ArrowDown") setOpen(true);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const r = results[active] ?? results[0];
      if (r) go(r);
    }
  };

  // Flatten index → used to highlight the active row across groups.
  let flatIndex = -1;

  return (
    <div
      ref={containerRef}
      className="relative flex-1 min-w-0 max-w-[420px]"
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <Search className="absolute start-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-text3 pointer-events-none" />
      <input
        ref={inputRef}
        type="text"
        aria-label={t("topbar.search")}
        placeholder={t("search.placeholder")}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        className="w-full h-9 ps-8 pe-4 sm:pe-14 bg-bg3 border border-border rounded-sm text-[13px] text-text outline-none transition-all placeholder:text-text3 focus:border-primary focus:bg-bg2"
      />
      <kbd className="absolute end-2 top-1/2 -translate-y-1/2 hidden sm:flex items-center gap-0.5 h-5 px-1.5 rounded-sm border border-border bg-bg2 text-[10px] font-semibold text-text3 pointer-events-none">
        Ctrl K
      </kbd>

      {/* Sous md, le panneau s'affranchit de la largeur (réduite) de l'input :
          `fixed` pleine largeur sous la topbar. */}
      {showDropdown && (
        <div className="absolute left-0 right-0 max-md:fixed max-md:inset-x-3 max-md:top-[calc(var(--topbar-h)+6px)] md:top-[calc(100%+6px)] z-[60] max-h-[60vh] overflow-y-auto scroll-thin rounded-md border border-border2 bg-card shadow-floating animate-fadeIn">
          {results.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-text3">{t("search.noResults")}</div>
          ) : (
            GROUP_ORDER.filter((g) => grouped.has(g)).map((group) => {
              const Icon = GROUP_ICON[group];
              return (
                <div key={group} className="py-1.5">
                  <div className="px-3 pt-1 pb-1 text-[9.5px] font-bold uppercase tracking-[0.1em] text-text3">
                    {t(`search.groups.${group}`)}
                  </div>
                  {grouped.get(group)!.map((r) => {
                    flatIndex += 1;
                    const isActive = flatIndex === active;
                    return (
                      <button
                        key={`${r.group}-${r.id}`}
                        type="button"
                        onMouseEnter={() => setActive(results.indexOf(r))}
                        onClick={() => go(r)}
                        className={cn(
                          "flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors",
                          isActive ? "bg-primary-dim" : "hover:bg-bg3/80",
                        )}
                      >
                        <Icon className={cn("h-3.5 w-3.5 shrink-0", isActive ? "text-primary" : "text-text3")} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[12.5px] font-medium text-text">{r.label}</span>
                          {r.sublabel && (
                            <span className="block truncate text-[10.5px] text-text3">{r.sublabel}</span>
                          )}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
